import React from 'react';
import { useNavigate } from 'react-router-dom';
import { mixed, object, string, number } from 'yup';

import { useToast } from 'Component/Alerta';
import BotaoLink from 'Component/BotaoLink';
import Cabecalho from 'Component/Cabecalho';
import Form, {
  Arquivo,
  Botao,
  CaixaDeTexto,
  Campo,
  Selecao,
} from 'Component/Form';
import Icone from 'Component/Icone';
import LayoutDefault from 'Component/Layout';
import Curriculo from 'Interface/Curriculos';
import { CURRICULOS, ESTADOS, MASCARA_TELEFONE } from 'View/constants';
import { useRecuperarCategoriasDeCurriculoQuery } from 'View/Service/Default/CategoriasDeCurriculo';
import { useAdicionarCurriculoMutation } from 'View/Service/Default/Curriculos';
import { useRecuperarDepartamentosQuery } from 'View/Service/Default/Departamentos';

const regras = object({
  departamento_id: number().required('Obrigatório'),
  categoria_id: number().required('Obrigatório'),
  nome: string().required('Obrigatório'),
  email: string().email('Não é válido').required('Obrigatório'),
  estado: string().required('Obrigatório'),
  cidade: string().required('Obrigatório'),
  telefone: string().required('Obrigatório'),
  vaga: string().required('Obrigatório'),
  mensagem: string().nullable().optional(),
  observacoes: string().nullable().optional(),
  url: mixed()
    .test('tamanho', 'O arquivo é muito grande', (valor) => {
      if (valor && valor.size) {
        return valor.size <= 500000;
      }
      return true;
    })
    .test('tipo', 'Formato não suportado', (valor) => {
      if (valor && valor.type) {
        return CURRICULOS.includes(valor.type);
      }
      return true;
    })
    .optional(),
});

const Adicionar = (): React.ReactElement => {
  const navegacao = useNavigate();
  const toast = useToast();
  const [adicionarCurriculo] = useAdicionarCurriculoMutation();

  const { data: { departamentos } = {} } = useRecuperarDepartamentosQuery({
    finder: 'listaJson',
    limit: 200,
    sort: 'nome',
  });

  const { data: { categorias } = {} } = useRecuperarCategoriasDeCurriculoQuery({
    finder: 'listaJson',
    limit: 200,
    sort: 'nome',
  });

  const enviar = async (dados: Curriculo): Promise<void> => {
    try {
      await adicionarCurriculo(dados).unwrap();

      toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });

      navegacao('/curriculos');
    } catch (error) {
      toast({
        titulo: 'Erro ao adicionar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  return (
    <LayoutDefault>
      <Cabecalho
        titulo="Adicionando currículo"
        descricao="Preencha o formulário e clique em enviar para adicionar o currículo"
      />
      <Form onSubmit={enviar} regras={regras}>
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="bg-white shadow-sm rounded p-4">
              <div className="row">
                <div className="col-md-6">
                  <Selecao
                    name="departamento_id"
                    rotulo="Departamento"
                    opcoes={departamentos}
                  />
                </div>
                <div className="col-md-6">
                  <Selecao
                    name="categoria_id"
                    rotulo="Categoria"
                    opcoes={categorias}
                  />
                </div>
                <div className="col-md-12">
                  <Campo name="nome" rotulo="Nome" />
                </div>
                <div className="col-md-8">
                  <Campo
                    name="email"
                    rotulo="E-mail"
                    prefixar={<Icone nome="envelope-fill" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="telefone"
                    rotulo="Telefone"
                    mascara={MASCARA_TELEFONE}
                    prefixar={<Icone nome="telephone-fill" />}
                  />
                </div>
                <div className="col-md-6">
                  <Selecao name="estado" rotulo="Estado" opcoes={ESTADOS} />
                </div>
                <div className="col-md-6">
                  <Campo name="cidade" rotulo="Cidade" />
                </div>
                <div className="col-md-12">
                  <Campo name="vaga" rotulo="Vaga" />
                </div>
                <div className="col-md-12">
                  <CaixaDeTexto name="mensagem" rotulo="Mensagem" />
                </div>
                <div className="col-md-12">
                  <CaixaDeTexto name="observacoes" rotulo="Observações" />
                </div>
              </div>
              <Arquivo name="url" />
              <div className="mt-5">
                <Botao>Enviar</Botao>
                <BotaoLink tema="light" to="/curriculo" className="ms-2">
                  Cancelar
                </BotaoLink>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </LayoutDefault>
  );
};

export default Adicionar;
